﻿import {currentCulture, ensureErrorType, ErrorBundle, errorToErrorBundle} from "@2d-front-monorepo/core";
import {Dialog} from "@2d-front-monorepo/design-library";
import {JSX, useEffect, useRef, useState} from 'react';

type ErrorDialogData = {
	error: ErrorBundle
	onCloseHandler?: () => void;
}

export function showErrorDialog(error: unknown, onCloseHandler?: () => void): void;
export function showErrorDialog(error: Error, onCloseHandler?: () => void): void;
export function showErrorDialog(error: ErrorBundle, onCloseHandler?: () => void): void;
export function showErrorDialog(error: ErrorBundle | Error | unknown, onCloseHandler?: () => void): void {
	if (error == null) {
		return;
	}

	let errorBundle: ErrorBundle;
	if (error instanceof Error) {
		errorBundle = errorToErrorBundle(error);

	} else if (typeof error === 'object' && 'title' in error && 'errors' in error && Array.isArray((error as any).errors)) {
		errorBundle = error as ErrorBundle;

	} else {
		errorBundle = errorToErrorBundle(ensureErrorType(error));
	}

	window.dispatchEvent(new CustomEvent('show-dialog', {
		detail: {error:errorBundle, onCloseHandler}
	}));
}

export default function ErrorDialog(): JSX.Element | null {
	const [data, setData] = useState<ErrorDialogData | null>(null);
	const dialogRef = useRef<HTMLDialogElement>(null);
	const locale = currentCulture();

	useEffect(() => {
		const handleShow = (e: Event) => setData((e as CustomEvent<ErrorDialogData>).detail);
		const handleHide = () => setData(null);

		window.addEventListener('show-dialog', handleShow);
		window.addEventListener('hide-dialog', handleHide);

		return () => {
			window.removeEventListener('show-dialog', handleShow);
			window.removeEventListener('hide-dialog', handleHide)
		}
	}, []);

	useEffect(() => {
		if (data && dialogRef.current) {
			const handleClose = () => {
				setData(null);
				data.onCloseHandler?.();
			};

			const dialog = dialogRef.current;
			dialog.addEventListener('close', handleClose);
			return () => dialog.removeEventListener('close', handleClose);
		}

		return () => {};
	}, [data]);

	return (data?.error ?
			<Dialog title={locale.format(data.error.title)} ref={dialogRef}>
				{data.error.detail ? <p>{locale.format(data.error.detail)}</p> : null}
				{data.error.errors.length > 0 ?
					<ul>
						{data.error.errors.map(((error, index) =>
							<li key={index}>{locale.format(error)}</li>))
						}
					</ul>
					: null}
				{data.error.footer ? <div><small>{locale.format(data.error.footer)}</small></div> : null}
			</Dialog>
			: null
	);
};
